import { useState, useRef, useEffect } from "react";
import {
  CheckCircle2,
  Copy,
  Download,
  Loader2,
  Clock,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  useCreateIntegration,
  useCloudTrailPollFreshness,
  useIntegrations,
} from "@/hooks/use-api";
import { useQueryClient } from "@tanstack/react-query";

// VITE_CLOUDTRAIL_BUS_ARN is set at build time from the Terraform output.
// Read lazily (inside a function) so tests can stub the env var via vi.stubEnv
// before the component renders.
function getBusArn(): string {
  // Use || so an empty-string env var also falls back to the placeholder.
  return (
    (import.meta.env.VITE_CLOUDTRAIL_BUS_ARN as string | undefined) ||
    "arn:aws:events:<region>:<account>:event-bus/<bus-name>"
  );
}

function isPlaceholderArn(arn: string): boolean {
  return !arn || arn.includes("<");
}

const TIMEOUT_MS = 5 * 60 * 1_000; // 5 minutes

const ACCOUNT_ID_RE = /^\d{12}$/;

// "form"       — not yet saved; show account ID input
// "waiting"    — saved; show correct CF template + polling for first event arrival
// "verified"   — first event confirmed via health_status=healthy
// "skipped"    — user bypassed verification; integration saved but events not confirmed
type Phase = "form" | "waiting" | "verified" | "skipped";

// buildCfTemplate generates the CloudFormation template for CloudTrail integration.
// `nonce` MUST be the backend verification_nonce returned by POST /api/v1/integrations
// so the ownership-proof SSM parameter name matches what the ingestion handler checks.
// The SSM::Parameter (secops-verify-<nonce>) is placed after the EventBridge rule so
// its CloudTrail PutParameter event is forwarded by the active rule and proves ownership.
function buildCfTemplate(busArn: string, nonce: string): string {
  const eventPatternJson = JSON.stringify({
    source: ["aws.cloudtrail"],
    "detail-type": [
      "AWS API Call via CloudTrail",
      "AWS Console Sign In via CloudTrail",
    ],
    detail: { readOnly: [false, { exists: false }] },
  });

  return [
    `AWSTemplateFormatVersion: "2010-09-09"`,
    `Description: "PrayingMantis CloudTrail integration"`,
    ``,
    `Resources:`,
    `  PrayingMantisEventsRole:`,
    `    Type: AWS::IAM::Role`,
    `    Properties:`,
    `      RoleName: prayingmantis-cloudtrail-events-role`,
    `      AssumeRolePolicyDocument:`,
    `        Version: "2012-10-17"`,
    `        Statement:`,
    `          - Effect: Allow`,
    `            Principal:`,
    `              Service: events.amazonaws.com`,
    `            Action: sts:AssumeRole`,
    `      Policies:`,
    `        - PolicyName: AllowPutEvents`,
    `          PolicyDocument:`,
    `            Version: "2012-10-17"`,
    `            Statement:`,
    `              - Effect: Allow`,
    `                Action: events:PutEvents`,
    `                Resource: "${busArn}"`,
    ``,
    `  CloudTrailForwardRule:`,
    `    Type: AWS::Events::Rule`,
    `    DependsOn: PrayingMantisEventsRole`,
    `    Properties:`,
    `      Name: prayingmantis-cloudtrail-forward`,
    `      Description: "Forward CloudTrail management write events to PrayingMantis"`,
    `      State: ENABLED`,
    `      EventPattern: '${eventPatternJson}'`,
    `      Targets:`,
    `        - Id: PrayingMantisSecurityBus`,
    `          Arn: "${busArn}"`,
    `          RoleArn: !GetAtt PrayingMantisEventsRole.Arn`,
    ``,
    `  # Ownership proof: PutParameter is captured by CloudTrail and forwarded by the`,
    `  # rule above. The ingestion handler matches the parameter name to activate the`,
    `  # integration. Safe to delete after the integration shows as active.`,
    `  OwnershipProof:`,
    `    Type: AWS::SSM::Parameter`,
    `    DependsOn: CloudTrailForwardRule`,
    `    Properties:`,
    `      Name: "secops-verify-${nonce}"`,
    `      Type: String`,
    `      Value: "prayingmantis-ownership-proof"`,
  ].join("\n");
}

interface CloudTrailStepProps {
  isCompleted: boolean;
}

export function CloudTrailStep({ isCompleted }: CloudTrailStepProps) {
  const qc = useQueryClient();

  const [phase, setPhase] = useState<Phase>("form");
  const [accountId, setAccountId] = useState("");
  const [region, setRegion] = useState("");
  const [accountIdError, setAccountIdError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [integrationId, setIntegrationId] = useState<string | null>(null);
  // backendNonce is set from the integration create response so the CF template
  // uses the same nonce stored in DynamoDB — making the proof flow coherent.
  const [backendNonce, setBackendNonce] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const createIntegration = useCreateIntegration();
  const cfTemplate = backendNonce
    ? buildCfTemplate(getBusArn(), backendNonce)
    : null;

  // On mount: check for an existing pending cloudtrail integration (configured
  // but not yet healthy). Handles page reload after saving but before first event.
  const { data: integrationsData } = useIntegrations();
  useEffect(() => {
    if (initialized || !integrationsData) return;
    // M3: only treat "unknown" as genuinely pending (no events yet).
    // "unhealthy" / "disabled" are distinct failure states, not pending delivery.
    const existing = integrationsData.integrations.find(
      (i) =>
        i.source_type === "cloudtrail" && i.health_status === "unknown",
    );
    if (existing) {
      setIntegrationId(existing.id);
      const existingNonce = existing.config?.verification_nonce;
      if (typeof existingNonce === "string" && existingNonce) {
        setBackendNonce(existingNonce);
      }
      setPhase("waiting");
      // I2: start the 5-min timeout so reload UX is consistent with new-create UX.
      const id = setTimeout(() => setTimedOut(true), TIMEOUT_MS);
      timeoutRef.current = id;
    }
    setInitialized(true);
  }, [initialized, integrationsData]);

  // Poll integrations list while waiting for first event.
  const { data: pollData } = useCloudTrailPollFreshness(
    integrationId,
    phase === "waiting",
  );

  // I5: move health-check state transition into an effect to avoid state
  // updates during render (would cause React invariant violations).
  useEffect(() => {
    if (phase !== "waiting" || !pollData || !integrationId) return;
    const integration = pollData.integrations.find(
      (i) => i.id === integrationId,
    );
    if (integration?.health_status === "healthy") {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      setPhase("verified");
      qc.invalidateQueries({ queryKey: ["onboarding"] });
    }
  }, [phase, pollData, integrationId, qc]);

  // M1: clean up the 5-minute timeout on unmount.
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // Declare busArn, handlers, and form-derived values before any conditional
  // return so they are always initialized — some are used in the waiting phase.
  const busArn = getBusArn();
  const arnIsPlaceholder = isPlaceholderArn(busArn);

  const handleCopy = async () => {
    if (!cfTemplate) return;
    await navigator.clipboard.writeText(cfTemplate);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    if (!cfTemplate) return;
    const blob = new Blob([cfTemplate], { type: "text/yaml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "prayingmantis-cloudtrail.yaml";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isCompleted) {
    return (
      <Card className="border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/30">
        <CardContent className="flex items-center gap-3 py-6">
          <CheckCircle2 className="h-6 w-6 shrink-0 text-green-600 dark:text-green-400" />
          <div>
            <p className="font-medium text-green-800 dark:text-green-300">
              Connected and verified
            </p>
            <p className="text-sm text-green-700 dark:text-green-400">
              AWS CloudTrail is connected and sending events.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (phase === "verified") {
    return (
      <Card className="border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/30">
        <CardContent className="flex items-center gap-3 py-6">
          <CheckCircle2 className="h-6 w-6 shrink-0 text-green-600 dark:text-green-400" />
          <div>
            <p className="font-medium text-green-800 dark:text-green-300">
              Events are arriving
            </p>
            <p className="text-sm text-green-700 dark:text-green-400">
              AWS CloudTrail is connected and sending events to PrayingMantis.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // C2: skipped shows "saved, not verified" — does NOT claim events are arriving.
  if (phase === "skipped") {
    return (
      <Card className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
        <CardContent className="flex items-start gap-3 py-6">
          <Clock className="mt-0.5 h-6 w-6 shrink-0 text-amber-600 dark:text-amber-400" />
          <div>
            <p className="font-medium text-amber-800 dark:text-amber-300">
              Integration saved — awaiting first event
            </p>
            <p className="text-sm text-amber-700 dark:text-amber-400">
              CloudFormation stack deployed but no events confirmed yet. Return
              to this step later to check whether events have arrived, or
              revisit Setup to resume the confirmation flow.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (phase === "waiting") {
    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold">AWS CloudTrail</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Integration saved. Deploy the CloudFormation stack below, then wait
            for the first event.
          </p>
        </div>

        {/* Show the CF template with the backend nonce so the proof event reaches the handler */}
        {cfTemplate ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>CloudFormation Template</Label>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleCopy}
                  aria-label="Copy CloudFormation template"
                >
                  <Copy className="mr-1.5 h-3.5 w-3.5" />
                  {copied ? "Copied!" : "Copy"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleDownload}
                  aria-label="Download CloudFormation template"
                >
                  <Download className="mr-1.5 h-3.5 w-3.5" />
                  Download
                </Button>
              </div>
            </div>
            {arnIsPlaceholder && (
              <Card className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
                <CardContent className="flex items-center gap-2 py-3">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    Bus ARN not configured — the template below uses a
                    placeholder. Contact support to complete setup.
                  </p>
                </CardContent>
              </Card>
            )}
            <Textarea
              value={cfTemplate}
              readOnly
              rows={10}
              className="font-mono text-xs"
              aria-label="CloudFormation template"
            />
            <p className="text-xs text-muted-foreground">
              Deploy this stack in your AWS account. It creates an EventBridge
              rule and an ownership-proof SSM parameter that activates the
              integration automatically.
            </p>
          </div>
        ) : (
          <Card className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
            <CardContent className="flex items-start gap-2 py-4">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                  CloudFormation template unavailable
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  The saved CloudTrail integration is missing its backend
                  verification token. Retry saving the integration or contact
                  support before deploying the stack.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="py-6">
            {timedOut ? (
              <div className="space-y-3">
                <p className="text-sm font-medium text-amber-700 dark:text-amber-400">
                  Still waiting — no events received yet.
                </p>
                <p className="text-sm text-muted-foreground">
                  Check that your CloudFormation stack reached{" "}
                  <span className="font-mono text-xs">CREATE_COMPLETE</span> and
                  that CloudTrail is enabled with Management events in the same
                  region.
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setTimedOut(false);
                    const id = setTimeout(() => setTimedOut(true), TIMEOUT_MS);
                    timeoutRef.current = id;
                  }}
                >
                  Retry
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin shrink-0 text-primary" />
                <div>
                  <p className="text-sm font-medium">
                    Waiting for first CloudTrail event&hellip;
                  </p>
                  <p className="text-sm text-muted-foreground">
                    The CloudFormation stack creates an SSM parameter that
                    activates your integration automatically.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* C2: skip advances to "saved but not verified", not "events arriving" */}
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground"
          onClick={() => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            setPhase("skipped");
          }}
        >
          Continue without confirming
        </Button>
      </div>
    );
  }

  // Phase: "form"
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAccountIdError(null);
    setSubmitError(null);

    if (!ACCOUNT_ID_RE.test(accountId)) {
      setAccountIdError(
        "Must be a 12-digit AWS account ID (e.g. 123456789012).",
      );
      return;
    }

    try {
      const result = await createIntegration.mutateAsync({
        source_type: "cloudtrail",
        display_name: "AWS CloudTrail",
        config: {
          aws_account_id: accountId,
          ...(region.trim() ? { region: region.trim() } : {}),
        },
      });
      setIntegrationId(result.integration.id);
      // Extract the backend nonce so the CF template uses the same value stored
      // in DynamoDB — the proof event must carry this exact nonce.
      const nonce = result.integration.config?.verification_nonce;
      if (typeof nonce === "string" && nonce) setBackendNonce(nonce);

      // Start the 5-minute timeout; cleared on unmount via useEffect cleanup.
      const id = setTimeout(() => setTimedOut(true), TIMEOUT_MS);
      timeoutRef.current = id;

      setPhase("waiting");
    } catch {
      setSubmitError("Failed to save integration. Please try again.");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">AWS CloudTrail</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Forward CloudTrail management write events to PrayingMantis via
          EventBridge. Deploy a CloudFormation stack in your AWS account — no
          IAM role for us to assume, no S3 bucket access required.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Account ID */}
        <div className="space-y-1.5">
          <Label htmlFor="aws_account_id">AWS Account ID</Label>
          <Input
            id="aws_account_id"
            type="text"
            inputMode="numeric"
            placeholder="123456789012"
            value={accountId}
            onChange={(e) => {
              setAccountId(e.target.value);
              if (accountIdError) setAccountIdError(null);
            }}
            aria-invalid={Boolean(accountIdError)}
            disabled={createIntegration.isPending}
          />
          {accountIdError && (
            <p className="text-sm text-red-600 dark:text-red-400" role="alert">
              {accountIdError}
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            Your 12-digit AWS account ID — click your account name in the
            top-right of the AWS console.
          </p>
        </div>

        {/* Region (optional) */}
        <div className="space-y-1.5">
          <Label htmlFor="aws_region">
            AWS Region{" "}
            <span className="font-normal text-muted-foreground">(optional)</span>
          </Label>
          <Input
            id="aws_region"
            type="text"
            placeholder="us-east-1"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            disabled={createIntegration.isPending}
          />
          <p className="text-xs text-muted-foreground">
            Defaults to us-east-1 if not specified.
          </p>
        </div>

        {/* Bottom hint */}
        <Card className="border-muted bg-muted/30">
          <CardContent className="flex items-start gap-3 py-4">
            <Clock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Click <strong>Save &amp; Get Template</strong> to create the
              integration. You will then receive a CloudFormation template
              pre-configured with a unique ownership token — deploy it in your
              AWS account to activate.
            </p>
          </CardContent>
        </Card>

        {submitError && (
          <p className="text-sm text-red-600 dark:text-red-400" role="alert">
            {submitError}
          </p>
        )}

        <Button type="submit" disabled={createIntegration.isPending}>
          {createIntegration.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving…
            </>
          ) : (
            "Save & Get Template"
          )}
        </Button>
      </form>
    </div>
  );
}
